import { useEffect, useMemo, useRef, useState } from 'react';
import { useLearner, useLearnerStore } from '@/state/learnerStore';
import { PERSONAS_SEED } from '@/seed/personas';
import { appHref } from '@/lib/nav';
import { AVATAR_IDS } from '@/constants/avatars';
import { COACH } from '@/constants/coach';
import AvatarMount, { type AvatarHandle } from '@/components/avatar/AvatarMount';
import AskCoachInput from '@/components/avatar/AskCoachInput';
import { useAvatarMic } from '@/lib/mic/useAvatarMic';
import LiveCaption from '@/components/avatar/LiveCaption';
import MetaCueChip from '@/components/avatar/MetaCueChip';
import ChatTranscript, { type ChatTurn } from '@/components/avatar/ChatTranscript';
import type { CoachMeta } from '@/lib/codebaby';
import { buildPlaybookStartData } from '@/lib/codebaby/startData';
import { estimateSpokenDuration } from '@/lib/codebaby/duration';
import { composePlaybookNarrative } from '@/lib/playbook/narrative';
import type { LearnerState, SavedPlaybookMoment } from '@/types/learner';
import type { SessionRecord } from '@/types/session';
import './PlaybookScreen.css';

/**
 * My Playbook — brief §4.5. Reads directly from learner_state.saved_playbook.
 * Filter chips derived from unique tags in the data. Remove prompts a
 * confirm so moments feel precious, not disposable.
 */

const TAG_META: Record<string, { label: string; color: 'platform' | 'win' | 'growth' }> = {
  discovery: { label: 'Discovery', color: 'platform' },
  pain_quantification: { label: 'Pain quantification', color: 'platform' },
  opening_hook: { label: 'Opening hook', color: 'platform' },
  small_yes_closes: { label: 'Small-yes closes', color: 'win' },
  commitment: { label: 'Commitment', color: 'win' },
  objection_reframe: { label: 'Objection reframe', color: 'growth' },
  proof_to_pain: { label: 'Proof-to-pain', color: 'growth' },
  composure: { label: 'Composure', color: 'growth' },
};

function tagMeta(tag: string) {
  return TAG_META[tag] ?? { label: prettify(tag), color: 'platform' as const };
}

function prettify(tag: string): string {
  return tag.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

export default function PlaybookScreen() {
  const learner = useLearner();
  const { saved_playbook } = learner;

  if (saved_playbook.length === 0) {
    return <PlaybookEmpty />;
  }
  return <PlaybookList learner={learner} />;
}

/* ----------------------------- Populated view ------------------------- */

function PlaybookList({ learner }: { learner: LearnerState }) {
  const { saved_playbook } = learner;

  const allTags = useMemo(() => {
    const set = new Set<string>();
    saved_playbook.forEach((m) => m.tags.forEach((t) => set.add(t)));
    return Array.from(set);
  }, [saved_playbook]);

  const [activeTag, setActiveTag] = useState<string>('all');
  const filtered = useMemo(
    () =>
      activeTag === 'all'
        ? saved_playbook
        : saved_playbook.filter((m) => m.tags.includes(activeTag)),
    [activeTag, saved_playbook],
  );

  const playsText = `${saved_playbook.length} play${saved_playbook.length === 1 ? '' : 's'} across ${allTags.length} skill${allTags.length === 1 ? '' : 's'}`;

  const startData = useMemo(() => buildPlaybookStartData(learner), [learner]);
  const narrative = useMemo(() => composePlaybookNarrative(saved_playbook), [saved_playbook]);
  const coachRef = useRef<AvatarHandle>(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [askInput, setAskInput] = useState('');
  const [captionText, setCaptionText] = useState('');
  const [coachMeta, setCoachMeta] = useState<CoachMeta | null>(null);
  const [transcript, setTranscript] = useState<ChatTurn[]>([]);
  const pendingAskRef = useRef<string | null>(null);

  const mic = useAvatarMic({
    toggle: () => coachRef.current?.microphoneToggle(),
    sessionReady: ready,
    onInterimText: (text) => setAskInput(text),
    onFinalText: (text) => {
      // Display only — the loader auto-asks on mic-final
      // (conversationConnection.js:200).
      recordLearnerTurn(text);
      setAskInput('');
    },
  });

  // Notify the coach's primary that the learner is on the playbook screen, so
  // the follow-up answers can reference the saved moments by context rather
  // than expecting startData (which was set at first mount and is now stale).
  useEffect(() => {
    if (!ready) return;
    coachRef.current?.notifyContext({
      type: 'navigation to',
      description: `The learner is reviewing their saved playbook (${saved_playbook.length} moments across ${allTags.length} skills).`,
      url: '/playbook',
    });
  }, [ready, saved_playbook.length, allTags.length]);

  const playLine = (text: string) => {
    if (!ready || playing || !text) return;
    coachRef.current?.playResponse(text);
  };

  const canPlay = ready && !playing;

  const recordLearnerTurn = (text: string) => {
    pendingAskRef.current = text;
    setTranscript((prev) => [...prev, { id: `learner-${Date.now()}`, role: 'learner', text }]);
  };

  const onAskSubmit = () => {
    const text = askInput.trim();
    if (!text) return;
    coachRef.current?.ask(text);
    recordLearnerTurn(text);
    setAskInput('');
  };

  /* mic-data is handled by useAvatarMic via mic.avatarProps */

  const onResponse = (r: { answerText: string }) => {
    const text = r.answerText;
    if (!text) return;
    setPlaying(true);
    if (pendingAskRef.current != null) {
      // Q&A reply: chat transcript only, leave caption alone so the playback
      // narrative still shows above.
      setTranscript((prev) => [...prev, { id: `coach-${Date.now()}`, role: 'coach', text }]);
      pendingAskRef.current = null;
    } else {
      // Scripted narration (Play All / Hear Coach's take) → caption.
      setCaptionText(text);
    }
  };

  const onMeta = (m: CoachMeta) => setCoachMeta(m);

  return (
    <div className="playbook">
      <CoachPlaybookHero
        coachRef={coachRef}
        startData={startData}
        playsText={playsText}
        onPlayerBuilt={() => setReady(true)}
        onResponse={onResponse}
        onSegmentEnded={() => setPlaying(false)}
        micProps={mic.avatarProps}
        onMeta={onMeta}
        onPlayAll={() => playLine(narrative)}
        narrativeDuration={estimateSpokenDuration(narrative)}
        canPlay={canPlay}
        playing={playing}
      />

      <div className="playbook__converse">
        {captionText && <LiveCaption script={captionText} />}
        <MetaCueChip meta={coachMeta} />
        <ChatTranscript turns={transcript} />
        <AskCoachInput
          value={askInput}
          onChange={setAskInput}
          onSubmit={onAskSubmit}
          onMicTap={mic.onTap}
          micState={mic.state}
          disabled={!ready}
        />
      </div>

      <nav className="playbook__filters" aria-label="Filter saved moments by skill">
        <FilterChip
          label="All"
          active={activeTag === 'all'}
          color="platform"
          onClick={() => setActiveTag('all')}
        />
        {allTags.map((t) => {
          const meta = tagMeta(t);
          return (
            <FilterChip
              key={t}
              label={meta.label}
              active={activeTag === t}
              color={meta.color}
              onClick={() => setActiveTag(t)}
            />
          );
        })}
      </nav>

      <ul className="playbook__list">
        {filtered.map((m) => (
          <MomentCard
            key={`${m.session_id}-${m.timestamp}`}
            moment={m}
            session={learner.session_history.find((s) => s.id === m.session_id)}
            onHearTake={() => playLine(m.coach_note)}
            canPlay={canPlay}
          />
        ))}
      </ul>
      {mic.overlays}
    </div>
  );
}

/* ------------------------------ Coach hero ---------------------------- */

function CoachPlaybookHero({
  coachRef,
  startData,
  playsText,
  onPlayerBuilt,
  onResponse,
  onSegmentEnded,
  micProps,
  onMeta,
  onPlayAll,
  narrativeDuration,
  canPlay,
  playing,
}: {
  coachRef: React.RefObject<AvatarHandle>;
  startData: ReturnType<typeof buildPlaybookStartData>;
  playsText: string;
  onPlayerBuilt: () => void;
  onResponse: (r: { answerText: string }) => void;
  onSegmentEnded: () => void;
  micProps: {
    onMicrophoneData: (d: { text: string; isFinal: boolean }) => void;
    onMicrophoneListening: () => void;
    onMicrophoneStopped: () => void;
  };
  onMeta: (m: CoachMeta) => void;
  onPlayAll: () => void;
  narrativeDuration: string;
  canPlay: boolean;
  playing: boolean;
}) {
  return (
    <section className="playbook-hero">
      <div className="playbook-hero__stage">
        <AvatarMount
          ref={coachRef}
          loaderId={AVATAR_IDS.coach}
          startData={startData}
          ariaLabel={`${COACH.fullName}, coach`}
          onPlayerBuilt={onPlayerBuilt}
          onResponse={onResponse}
          onSegmentEnded={onSegmentEnded}
          {...micProps}
          onMeta={onMeta}
        />
      </div>
      <div className="playbook-hero__body">
        <p className="playbook-hero__crumb">My Playbook</p>
        <p className="playbook-hero__tagline">
          Moments you've saved, organized by skill. These are your strongest plays — bring them to
          the real room.
        </p>
        <div className="playbook-hero__footer">
          <span className="playbook-hero__count">{playsText}</span>
          <button
            type="button"
            className="playbook-hero__play"
            onClick={onPlayAll}
            disabled={!canPlay}
            aria-pressed={playing}
          >
            <PlayIcon /> {playing ? 'Playing…' : `Play all my wins · ${narrativeDuration}`}
          </button>
        </div>
      </div>
    </section>
  );
}

function FilterChip({
  label,
  active,
  color,
  onClick,
}: {
  label: string;
  active: boolean;
  color: 'platform' | 'win' | 'growth';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`chip chip--${color} ${active ? 'chip--active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

function MomentCard({
  moment,
  session,
  onHearTake,
  canPlay,
}: {
  moment: SavedPlaybookMoment;
  session: SessionRecord | undefined;
  onHearTake: () => void;
  canPlay: boolean;
}) {
  const removePlaybookMoment = useLearnerStore((s) => s.removePlaybookMoment);
  const primaryTag = moment.tags[0] ?? 'discovery';
  const meta = tagMeta(primaryTag);

  const personaName = session ? PERSONAS_SEED[session.persona_id].display_name : null;
  const attempt = session?.attempt_number;
  const date = session?.started_at ? formatDate(session.started_at) : null;

  const onRemove = () => {
    const ok = window.confirm('Remove this from your playbook?');
    if (!ok) return;
    removePlaybookMoment(moment.session_id, moment.timestamp);
  };

  return (
    <li className="play">
      <span className="play__icon" aria-hidden>
        <BookmarkIcon />
      </span>
      <div className="play__body">
        <blockquote className="play__quote">{moment.quote}</blockquote>
        <p className="play__meta">
          {personaName && (
            <>
              From: <strong>{personaName}</strong>
            </>
          )}
          {attempt !== undefined && <> · Attempt {attempt}</>}
          <> · {formatDuration(moment.timestamp)} mark</>
          {date && <> · {date}</>}
        </p>
        <p className="play__note">{moment.coach_note}</p>
        <div className="play__footer">
          <span className={`play__tag play__tag--${meta.color}`}>{meta.label}</span>
          {moment.tags.slice(1).map((t) => {
            const m = tagMeta(t);
            return (
              <span key={t} className={`play__tag play__tag--${m.color} play__tag--muted`}>
                {m.label}
              </span>
            );
          })}
          <button type="button" className="play__coach" onClick={onHearTake} disabled={!canPlay}>
            <PlayIcon /> Hear {COACH.firstName}'s take · {estimateSpokenDuration(moment.coach_note)}
          </button>
          <button
            type="button"
            className="play__remove"
            onClick={onRemove}
            aria-label="Remove from playbook"
          >
            Remove
          </button>
        </div>
      </div>
    </li>
  );
}

/* ------------------------------ Empty state --------------------------- */

function PlaybookEmpty() {
  return (
    <div className="playbook playbook--empty">
      <div className="playbook__empty">
        <span className="playbook__empty-art" aria-hidden>
          <BookmarkPlusIcon />
        </span>
        <h1 className="playbook__empty-headline">
          Your playbook starts with your first saved win.
        </h1>
        <p className="playbook__empty-copy">
          After a roleplay debrief, tap "Save to playbook" on any win moment — it'll show up here,
          grouped by skill. Try your first session.
        </p>
        {/* Plain anchor — coach↔persona transitions force a full page load. */}
        <a href={appHref('/roleplay/scn-priya')} className="playbook__empty-cta">
          Start a practice session <ArrowRight />
        </a>
      </div>
    </div>
  );
}

/* ----------------------------- Utilities ------------------------------ */

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/* -------------------------------- Icons ------------------------------- */

function BookmarkIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M6 4h12v17l-6-3.5L6 21V4z" fill="currentColor" />
    </svg>
  );
}

function BookmarkPlusIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 4h12v17l-6-3.5L6 21V4z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M12 8v6M9 11h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden>
      <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
    </svg>
  );
}

function ArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12h14M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
